import { Injectable } from '@angular/core';

import { WidgetChartOptions } from 'app/modules/widget/components/widget-chart/widget-chart.component';
import {
  MetricScaleMin,
  WidgetPredictiveOptions,
} from 'app/modules/widget/components/widget-predictive/widget-predictive.component';
import { EnableClassTelemetry } from 'app/services/otel.service';
import {
  Dashboard,
  DASHBOARD_VERSION,
  DASHBOARD_WIDTH,
  Widget,
} from './bridge-api/dashboard.service';
import { FieldService } from './storage-api/field.service';

@EnableClassTelemetry()
@Injectable({
  providedIn: 'root',
})
export class DashboardMigrationService {
  constructor() {}

  static checkIfNeedsMigration(dashboard: Dashboard) {
    return dashboard.dashboard_version !== DASHBOARD_VERSION;
  }

  static migrateToCurrent(dashboard: Dashboard, migrationContext?: any) {
    DashboardMigrationService.doMigration(
      dashboard.dashboard_version,
      DASHBOARD_VERSION,
      dashboard,
      migrationContext
    );
  }

  static doMigration(from: number, to: number, dashboard: Dashboard, migrationContext?: any) {
    if (from !== to) {
      let curr = from;
      while (curr !== to) {
        const next = to > from ? curr + 1 : curr - 1;
        const migratorFn = DashboardMigrationService[`migrateFrom${curr}To${next}`];

        if (typeof migratorFn !== 'function') {
          throw new Error(`No migration function from ${curr} to ${next}!`);
        }

        console.log(`Migrating Dashboard ${dashboard.id} from version ${curr} to ${next}...`);

        migratorFn(dashboard, migrationContext);
        curr = next;
        dashboard.dashboard_version = curr;

        console.log(`Dashboard ${dashboard.id} migrated successfully.`);
      }
    }
  }

  static migrateFrom2To3(dashboard: Dashboard, _migrationContext) {
    dashboard.width = DASHBOARD_WIDTH;

    dashboard.widgets.forEach((w) => (w.size[0] = 2 * w.size[0]));
  }

  static migrateFrom3To2(dashboard: Dashboard, _migrationContext) {
    dashboard.width = 6;

    dashboard.widgets.forEach((w) => (w.size[0] = Math.round(w.size[0] / 2)));
  }

  static migrateFrom3To4(dashboard: Dashboard, _migrationContext) {
    dashboard.widgets.forEach((widget) => delete widget.payload.thumbnail);
  }

  static migrateFrom4To3(_dashboard: Dashboard, _migrationContext) {
    //There's no sane way to restore thumbnails
  }

  static migrateFrom4To5(dashboard: Dashboard, _migrationContext) {
    dashboard.widgets.forEach((widget) => {
      if (widget.payload.dataSource) {
        const dataSource = JSON.parse(JSON.stringify(widget.payload.dataSource));
        widget.dataSourceId = dataSource.id;
        delete widget.payload.dataSource;
      }
    });
  }

  static migrateFrom5To4(_dashboard: Dashboard, _migrationContext) {
    //There's no sane way to restore widget datasource
  }

  static migrateFrom5To6(dashboard: Dashboard, _migrationContext) {
    dashboard.payload.pages = [{ title: 'Page 1', id: 1 }];

    dashboard.widgets.forEach((w) => (w.payload.page = 1));

    Object.values(dashboard.widgetGroups).forEach((dfg) => (dfg.page = 1));
  }

  static migrateFrom6To5(dashboard: Dashboard, _migrationContext) {
    delete dashboard.payload.pages;

    dashboard.widgets.forEach((w) => delete w.payload.page);

    Object.values(dashboard.widgetGroups).forEach((dfg) => delete dfg.page);
  }

  static migrateFrom6To7(dashboard: Dashboard, _migrationContext) {
    dashboard.payload.pages.forEach((p) => {
      let row = 0;
      let nextRow = 0;
      let column = 0;

      dashboard.widgets
        .filter((w) => w.payload.page === p.id)
        .forEach((w) => {
          w.cols = w.size[0];
          w.rows = w.size[1] * 4;

          if (column + w.size[0] > 12) {
            column = 0;
            row = nextRow;
          } else {
            nextRow = Math.max(nextRow, row + w.rows);
          }

          w.x = column;
          w.y = row;

          column += w.size[0];
        });
    });
  }

  static migrateFrom7To6(dashboard: Dashboard, _migrationContext) {
    dashboard.widgets.forEach((w) => {
      w.size[0] = w.cols;
      w.size[1] = w.rows / 4;

      delete w.cols;
      delete w.rows;
      delete w.x;
      delete w.y;
    });
  }

  static migrateFrom7To8(dashboard: Dashboard, _migrationContext) {
    (
      dashboard.widgets.filter(
        (w) => w.payload.type === 'numericMetadata'
      ) as Widget<WidgetChartOptions>[]
    ).forEach((w) => {
      w.payload.numericMetadataFields = [
        {
          fieldId: w.payload.analyticItem,
          label: FieldService.getFieldLongName(w.payload.analyticItem),
          scoring: w.payload.aggregator === 'AVG' ? 'avg' : 'sum',
          color: {
            red: 0,
            green: 88,
            blue: 255,
            alpha: 1,
          },
        },
      ];

      w.payload.legends = {};
      w.payload.legends[`${FieldService.getFieldLongName(w.payload.analyticItem)}`] = true;

      delete w.payload.customItemColors;
      delete w.payload.analyticItem;
      delete w.payload.aggregator;
    });
  }

  static migrateFrom8To7(dashboard: Dashboard, _migrationContext) {
    (
      dashboard.widgets.filter(
        (w) => w.payload.type === 'numericMetadata'
      ) as Widget<WidgetChartOptions>[]
    ).forEach((w) => {
      const field = w.payload.numericMetadataFields[0];
      w.payload.analyticItem = field.fieldId;
      w.payload.aggregator = field.scoring === 'sum' ? 'SUM' : 'AVG';

      w.payload.legends = {};
      w.payload.legends[`${FieldService.getFieldLongName(field.fieldId)}`] = true;

      delete w.payload.numericMetadataFields;
    });
  }

  static migrateFrom8To9(dashboard: Dashboard, _migrationContext) {
    dashboard.comparisons = dashboard.comparisons ?? {};
  }

  static migrateFrom9To8(dashboard: Dashboard, _migrationContext) {
    delete dashboard.comparisons;
  }

  static migrateFrom9To10(dashboard: Dashboard, _migrationContext) {
    dashboard.payload.actionPlans = [];
  }

  static migrateFrom10To9(dashboard: Dashboard, _migrationContext) {
    delete dashboard.payload.actionPlans;
  }

  static migrateFrom10To11(dashboard: Dashboard, _migrationContext) {
    dashboard.payload.pins = [];
  }

  static migrateFrom11To10(dashboard: Dashboard, _migrationContext) {
    delete dashboard.payload.pins;
  }

  static migrateFrom11To12(dashboard: Dashboard, _migrationContext) {
    (
      dashboard.widgets.filter(
        (w) => w.payload.type === 'breakdown'
      ) as Widget<WidgetChartOptions>[]
    ).forEach((w) => {
      w.payload.limit = 20;
    });
  }

  static migrateFrom12To11(dashboard: Dashboard, _migrationContext) {
    (
      dashboard.widgets.filter(
        (w) => w.payload.type === 'breakdown'
      ) as Widget<WidgetChartOptions>[]
    ).forEach((w) => {
      delete w.payload.limit;
    });
  }

  static migrateFrom12To13(dashboard: Dashboard, _migrationContext) {
    (
      dashboard.widgets.filter(
        (w) => w.payload.type === 'predictor'
      ) as Widget<WidgetPredictiveOptions>[]
    ).forEach((w) => {
      w.payload.stepValue ??= '10%';
      w.payload.view ??= 'predictor';
      w.payload.scatterValue ??= 'Median';

      w.payload.decimals = '1';
      w.payload.displayLabels = false;
    });
  }

  static migrateFrom13To12(dashboard: Dashboard, _migrationContext) {
    (
      dashboard.widgets.filter(
        (w) => w.payload.type === 'breakdown'
      ) as Widget<WidgetPredictiveOptions>[]
    ).forEach((w) => {
      delete w.payload.decimals;
      delete w.payload.displayLabels;
    });
  }

  static migrateFrom13To14(dashboard: Dashboard, _migrationContext) {
    (
      dashboard.widgets.filter(
        (w) => w.payload.type === 'predictor'
      ) as Widget<WidgetPredictiveOptions>[]
    ).forEach((w) => {
      const mainRange = (w.payload.dependentRange ?? '1:10').split(':');
      w.payload.mainSettings = {
        scoring: w.payload.breakdownScoringStrategy ?? 'AVERAGE',
        boxDirection:
          w.payload.breakdownBoxDirection ??
          (w.payload.breakdownScoringStrategy === 'BOX' ? 'TOP' : undefined),
        boxSize:
          w.payload.breakdownBoxSize ??
          (w.payload.breakdownScoringStrategy === 'BOX' ? '1' : undefined),
        min: mainRange[0].toString() as MetricScaleMin,
        max: parseInt(mainRange[1], 10),
      };

      const driversRange = (w.payload.driversRange ?? '1:10').split(':');
      w.payload.driverSettings = {
        scoring: w.payload.driverScoringStrategy ?? 'AVERAGE',
        boxDirection:
          w.payload.driverBoxDirection ??
          (w.payload.driverScoringStrategy === 'BOX' ? 'TOP' : undefined),
        boxSize:
          w.payload.driverBoxSize ?? (w.payload.driverScoringStrategy === 'BOX' ? '1' : undefined),
        min: driversRange[0].toString() as MetricScaleMin,
        max: parseInt(driversRange[1], 10),
      };

      delete w.payload.breakdownScoringStrategy;
      delete w.payload.breakdownBoxDirection;
      delete w.payload.breakdownBoxSize;
      delete w.payload.dependentRange;
      delete w.payload.driverScoringStrategy;
      delete w.payload.driverBoxDirection;
      delete w.payload.driverBoxSize;
      delete w.payload.driversRange;
    });
  }

  static migrateFrom14To13(dashboard: Dashboard, _migrationContext) {
    (
      dashboard.widgets.filter(
        (w) => w.payload.type === 'breakdown'
      ) as Widget<WidgetPredictiveOptions>[]
    ).forEach((w) => {
      delete w.payload.mainSettings;
      delete w.payload.driverSettings;
    });
  }

  static migrateFrom14To15(dashboard: Dashboard, _migrationContext) {
    (
      dashboard.widgets.filter(
        (w) => w.payload.type === 'predictor'
      ) as Widget<WidgetPredictiveOptions>[]
    ).forEach((w) => {
      w.payload.simulations = w.payload.simulations ?? [];
    });
  }

  static migrateFrom15To14(_dashboard: Dashboard, _migrationContext) {}

  static migrateFrom15To16(dashboard: Dashboard, _migrationContext) {
    dashboard.widgets
      .filter((widget) => widget.payload.newspaper)
      .forEach((widget) => (widget.payload.newspaper.timezone = dashboard.payload.timezone));
  }

  static migrateFrom16To15(dashboard: Dashboard, _migrationContext) {
    dashboard.widgets
      .filter((widget) => widget.payload.newspaper)
      .forEach((widget) => delete widget.payload.newspaper.timezone);
  }

  static migrateFrom16To17(dashboard: Dashboard, _migrationContext) {
    dashboard.widgets
      .filter((widget) => widget.payload.selectedSection)
      .forEach((widget) => {
        if (!Array.isArray(widget.payload.selectedSection)) {
          widget.payload.selectedSection = [widget.payload.selectedSection];
        }
      });
  }

  static migrateFrom17To16(dashboard: Dashboard, _migrationContext) {
    dashboard.widgets
      .filter((widget) => widget.payload.selectedSection)
      .forEach((widget) => {
        if (Array.isArray(widget.payload.selectedSection)) {
          widget.payload.selectedSection = widget.payload.selectedSection[0];
        }
      });
  }

  static migrateFrom17To18(dashboard: Dashboard, _migrationContext) {
    dashboard.widgets
      .filter((widget) => widget.payload.sectionFilter)
      .forEach((widget) => {
        if (!Array.isArray(widget.payload.sectionFilter)) {
          widget.payload.sectionFilter = [widget.payload.sectionFilter];
        }
      });
  }

  static migrateFrom18To17(dashboard: Dashboard, _migrationContext) {
    dashboard.widgets
      .filter((widget) => widget.payload.sectionFilter)
      .forEach((widget) => {
        if (Array.isArray(widget.payload.sectionFilter)) {
          (widget.payload as any).sectionFilter = widget.payload.sectionFilter[0];
        }
      });
  }

  static migrateFrom18To19(dashboard: Dashboard, _migrationContext) {
    dashboard.widgets.forEach((widget) => {
      // Add sectionProperties filter set if filters exist but sectionProperties is missing
      if (widget.payload.filters) {
        widget.payload.filters.sectionProperties ??= { and: [], or: [], not: [] };
      }

      delete widget.layerIndex;
    });
  }

  static migrateFrom19To18(dashboard: Dashboard, _migrationContext) {
    dashboard.widgets.forEach((widget) => {
      if (widget.payload.filters?.sectionProperties) {
        delete widget.payload.filters.sectionProperties;
      }

      delete widget.layerIndex;
    });
  }
}
